// src/features/admissions/AdmissionTreatmentPanel.tsx
import React from "react";
import { Box, Text, Spinner, VStack } from "@chakra-ui/react";
import { useGetTreatmentsByAppointmentId } from "../../apis/patients";

type Props = {
  admissionId: string;
  appointmentId?: string;
};

export default function AdmissionTreatmentPanel({
  admissionId,
  appointmentId,
}: Props) {
  // --- FIX: always call the hook (React rule) ---
  const {
    data: treatmentData,
    isLoading,
    isError,
  } = useGetTreatmentsByAppointmentId(appointmentId || "");

  // If no appointment ID exists, we can't load anything
  if (!appointmentId) {
    return (
      <Box borderWidth="1px" borderRadius="md" p={3} bg="white">
        <Text fontSize="sm" color="gray.500">
          No linked appointment found for this admission. Treatment plan not
          available.
        </Text>
      </Box>
    );
  }

  // Normalize dataset
  const treatments = Array.isArray(treatmentData) ? treatmentData : [];

  if (isLoading) {
    return (
      <Box borderWidth="1px" borderRadius="md" p={3} bg="white">
        <Spinner size="sm" />
        <Text ml={2} as="span" fontSize="sm">
          Loading treatment plan…
        </Text>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box borderWidth="1px" borderRadius="md" p={3} bg="white">
        <Text fontSize="sm" color="red.500">
          Failed to load treatment plan for this admission.
        </Text>
      </Box>
    );
  }

  if (!treatments.length) {
    return (
      <Box borderWidth="1px" borderRadius="md" p={3} bg="white">
        <Text fontSize="sm" color="gray.500">
          No treatment orders found for the admitting appointment yet.
        </Text>
      </Box>
    );
  }

  return (
    <Box borderWidth="1px" borderRadius="md" p={3} bg="white">
      <VStack align="stretch" spacing={3}>
        {treatments.map((item: any, i: number) => (
          <Box
            key={item._id || i}
            borderWidth="1px"
            borderRadius="md"
            p={2}
            bg="gray.50"
          >
            <Text mb={1} fontWeight="bold">
              {item.name || "Unnamed treatment"}
            </Text>

            {item.route && (
              <Text fontSize="sm">
                <b>Route:</b> {item.route}
              </Text>
            )}
            {item.dosage && (
              <Text fontSize="sm">
                <b>Dosage:</b> {item.dosage}
              </Text>
            )}
            {item.frequency && (
              <Text fontSize="sm">
                <b>Frequency:</b> {item.frequency}
              </Text>
            )}
            {item.duration && (
              <Text fontSize="sm">
                <b>Duration:</b> {item.duration}
              </Text>
            )}
            {item.notes && (
              <Text mt={1} fontSize="sm">
                <b>Notes:</b> {item.notes}
              </Text>
            )}
          </Box>
        ))}
      </VStack>
    </Box>
  );
}
